import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { differenceInMilliseconds, isSameDay, startOfTomorrow } from 'date-fns';
import { Subscription } from 'rxjs';

import { resizeRxUtils, setCssPropertyValue } from '@blaze-shared/shared-util';

import { CalendarConfig } from '../calendar.models';
import { CALENDAR_CONFIG } from '../providers/config';

@Directive({
  selector: '[blazeTimeTrack]',
  standalone: false,
})
export class TimeTrackDirective implements OnChanges, AfterViewInit, OnDestroy {
  private el = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private readonly config = inject<CalendarConfig>(CALENDAR_CONFIG);

  @Input('blazeTimeTrack') singleCalendar?: boolean;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('scrollAllDayToHour') calendarsInfo?: { idx: number; count: number };
  // for triggering position update purposes
  @Input() availableHours?: { min: number; max: number };
  @Input() selectedDate?: Date;
  @Input() timeScale = 1;

  private ticker?: HTMLDivElement;
  private tickerTimeout?: ReturnType<typeof setTimeout>;
  private tickerUpdates?: ReturnType<typeof setInterval>;
  private tickerResizes?: Subscription;

  get isToday(): boolean {
    return !!this.selectedDate && isSameDay(new Date(), this.selectedDate);
  }

  ngOnChanges(): void {
    setTimeout(() => this.updateTickerPosition());
  }

  ngAfterViewInit(): void {
    this.setupResume();
  }

  ngOnDestroy(): void {
    this.eventsTeardown();
    if (this.tickerTimeout) {
      clearTimeout(this.tickerTimeout);
    }
  }

  private eventsSetup(): void {
    if (!this.ticker) {
      const ticker = document.createElement('div');
      ticker.className = 'calendar-time-ticker';
      ticker.style.display = 'none';
      ticker.style.top = 'var(--calendar-time-ticker-top)';
      ticker.style.width = 'var(--calendar-time-ticker-width)';
      this.ticker = ticker;
      this.el.nativeElement.append(ticker);
    }
    this.tickerUpdates = setInterval(() => this.updateTickerPosition(), 60_000);
    this.tickerResizes = resizeRxUtils
      .fromResizeObserver(this.el.nativeElement.parentElement)
      .subscribe(() => this.updateTickerPosition());
  }

  private eventsTeardown(): void {
    if (this.tickerUpdates) {
      clearInterval(this.tickerUpdates);
    }
    if (this.tickerResizes) {
      this.tickerResizes.unsubscribe();
      this.tickerResizes = undefined;
    }
  }

  private setupResume(): void {
    if (this.selectedDate && this.isToday) {
      return;
    }

    // if it's not today, wait till tomorrow & try updating the ui
    const timeout = differenceInMilliseconds(startOfTomorrow(), new Date()) + 1000;
    this.tickerTimeout = setTimeout(() => {
      this.tickerTimeout = undefined;
      if (!this.updateTickerPosition()) {
        // if still not today, try tomorrow
        this.setupResume();
      }
    }, timeout);
  }

  private updateTickerPosition(): boolean {
    const now = new Date();
    const hrs = now.getHours();
    const mins = now.getMinutes();
    const hrsBlock =
      this.isToday && (this.singleCalendar || (this.calendarsInfo?.idx ?? 0) === 0)
        ? (this.el.nativeElement.querySelector(`[data-hrs="${hrs}"]`) as HTMLDivElement)
        : undefined;
    let display = 'block';
    if (hrsBlock) {
      const yPos =
        hrsBlock.offsetTop + Math.round(((mins / 60) * this.config.oneHourHeight) / this.timeScale);
      setCssPropertyValue('--calendar-time-ticker-top', `${yPos}px`, this.el.nativeElement);
    } else {
      display = 'none';
    }
    setCssPropertyValue(
      '--calendar-time-ticker-width',
      `${this.el.nativeElement.clientWidth * (this.singleCalendar ? 1 : (this.calendarsInfo?.count ?? 1))}px`,
      this.el.nativeElement,
    );

    let result = false;
    if (this.ticker?.style.display !== display) {
      if (display === 'block') {
        this.eventsSetup();
        result = true;
      } else {
        this.eventsTeardown();
      }
      if (this.ticker) {
        this.ticker.style.display = display;
      }
    }
    return result;
  }
}
